import React from 'react';
import { Button } from '@components/primitives/Button';
import CloseIcon from '@mui/icons-material/Close';
import { CircularProgress, DialogActions, DialogContentText, Typography } from '@mui/material';

import {
  CloseButton,
  ContentColumn,
  ContentWrapper,
  IconColumn,
  StyledDialog,
} from './ConfirmDialog.styles';
import type { ConfirmDialogProps } from './ConfirmDialog.types';
import { VARIANT_CONFIG } from './ConfirmDialog.utils';

/**
 * Primitive ConfirmDialog component with semantic variants
 *
 * Features:
 * - Colored left border matching variant
 * - Icon without background
 * - White background
 * - Large primary buttons (cancel=outlined, confirm=contained)
 * - Loading state support
 *
 * @example
 * ```tsx
 * <ConfirmDialog
 *   open={open}
 *   variant="warning"
 *   title="Delete Item?"
 *   message="This action cannot be undone."
 *   onClose={handleClose}
 *   onConfirm={handleConfirm}
 *   confirmText="Delete"
 * />
 * ```
 */
export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  variant = 'info',
  title,
  message,
  children,
  onClose,
  onConfirm,
  cancelText = 'Cancel',
  confirmText = 'Confirm',
  hideCancel = false,
  loading = false,
  disableConfirm = false,
  maxWidth = 'sm',
  fullWidth = true,
}) => {
  const variantConfig = VARIANT_CONFIG[variant];
  const Icon = variantConfig.icon;

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      variantColor={variantConfig.color}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
    >
      {/* Absolutely positioned close button */}
      <CloseButton onClick={onClose} size="small">
        <CloseIcon />
      </CloseButton>

      {/* Two-column layout: Icon column + Content column */}
      <ContentWrapper>
        {/* Icon column */}
        <IconColumn>
          <Icon sx={{ fontSize: 24, color: variantConfig.color }} />
        </IconColumn>

        {/* Content column */}
        <ContentColumn>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          {message && <DialogContentText>{message}</DialogContentText>}
          {children}
        </ContentColumn>
      </ContentWrapper>

      {/* Actions - bottom padding matches left/right */}
      <DialogActions sx={{ padding: '16px 24px 24px 24px' }}>
        {!hideCancel && (
          <Button onClick={onClose} variant="outlined" size="large" disabled={loading}>
            {cancelText}
          </Button>
        )}
        <Button onClick={onConfirm} variant="contained" size="large" disabled={loading || disableConfirm}>
          {loading ? <CircularProgress size={20} color="inherit" /> : confirmText}
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};
